import { api, unwrap } from "@lumberset/api-client";
import { bpsToPercent, percentToBps } from "@lumberset/takeoff-ui";
import {
  Button,
  Container,
  Field,
  Input,
  RadioGroup,
  SectionHeader,
  SectionRow,
  Select,
  Textarea,
  toast,
} from "@lumberset/ui";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { yardQuery } from "../../../lib/queries";
import {
  type BuilderTrade,
  builderTrades,
  tradeLabels,
  workspaceKindLabels,
} from "../../../lib/workspace";
import { LogoSection } from "./-logo";

export const Route = createFileRoute("/_app/settings/")({ component: YardSettings });

function YardSettings() {
  const queryClient = useQueryClient();
  const { data: yard } = useSuspenseQuery(yardQuery);
  const profile = yard.profile;
  const canEdit = yard.role !== "member";
  const builder = yard.kind === "builder";
  const [reviewMode, setReviewMode] = useState<"gated" | "audit">(profile?.reviewMode ?? "gated");
  const [trade, setTrade] = useState<BuilderTrade>(
    (yard.trade as BuilderTrade | null) ?? "general_contractor",
  );

  const save = useMutation({
    mutationFn: (form: FormData) => {
      const text = (name: string) => String(form.get(name) ?? "").trim() || null;
      return unwrap(
        api.yard.profile.$patch({
          json: {
            legalName: text("legalName"),
            phone: text("phone"),
            email: text("email"),
            website: text("website"),
            address: {
              line1: text("line1") ?? undefined,
              city: text("city") ?? undefined,
              region: text("region") ?? undefined,
              postalCode: text("postalCode") ?? undefined,
            },
            reviewMode,
            // A Builder has no quotes to default, so those fields are left as they are.
            ...(builder
              ? { trade }
              : {
                  defaultMarkupBps: percentToBps(Number(form.get("markup") || 0)),
                  taxRateBps: percentToBps(Number(form.get("tax") || 0)),
                  quoteValidDays: Number(form.get("validDays") || 30),
                  quoteTerms: text("terms"),
                }),
          },
        }),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["yard"] });
      toast.success(builder ? "Company settings saved" : "Yard settings saved");
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate(new FormData(event.currentTarget));
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-y-3">
      <fieldset disabled={!canEdit} className="contents">
        <Container className="divide-y p-0">
          <SectionHeader
            title="Workspace"
            description={
              builder
                ? "A builder workspace: takeoffs, budget estimates and requests for quotes."
                : "A lumber yard workspace: takeoffs priced from your catalog into quotes."
            }
          />
          <SectionRow label="Kind">
            {workspaceKindLabels[yard.kind]}
            <span className="txt-compact-xsmall block text-ui-fg-muted">
              Contact support to change the kind of workspace.
            </span>
          </SectionRow>
          {builder ? (
            <div className="px-6 py-4 sm:max-w-xs">
              <Field label="What you do">
                {(control) => (
                  <Select value={trade} onValueChange={(value) => setTrade(value as BuilderTrade)}>
                    <Select.Trigger id={control.id}>
                      <Select.Value />
                    </Select.Trigger>
                    <Select.Content>
                      {builderTrades.map((value) => (
                        <Select.Item key={value} value={value}>
                          {tradeLabels[value]}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select>
                )}
              </Field>
            </div>
          ) : null}
        </Container>

        <Container className="divide-y p-0">
          <SectionHeader
            title={builder ? "On your requests for quotes" : "On your quotes"}
            description={
              builder
                ? "What yards see at the top of every request you send them."
                : "What your customers see at the top of every quote."
            }
          />
          <div className="grid gap-5 px-6 py-5 sm:grid-cols-2">
            <Field label="Business name" hint={`Shown instead of "${yard.name}" when filled in.`}>
              {(control) => (
                <Input {...control} name="legalName" defaultValue={profile?.legalName ?? ""} />
              )}
            </Field>
            <Field label="Email for replies" optional>
              {(control) => (
                <Input {...control} name="email" type="email" defaultValue={profile?.email ?? ""} />
              )}
            </Field>
            <Field label="Phone" optional>
              {(control) => (
                <Input {...control} name="phone" type="tel" defaultValue={profile?.phone ?? ""} />
              )}
            </Field>
            <Field label="Website" optional>
              {(control) => (
                <Input {...control} name="website" defaultValue={profile?.website ?? ""} />
              )}
            </Field>
            <Field label="Street address" optional className="sm:col-span-2">
              {(control) => (
                <Input {...control} name="line1" defaultValue={profile?.address?.line1 ?? ""} />
              )}
            </Field>
            <Field label="City" optional>
              {(control) => (
                <Input {...control} name="city" defaultValue={profile?.address?.city ?? ""} />
              )}
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="State" optional>
                {(control) => (
                  <Input {...control} name="region" defaultValue={profile?.address?.region ?? ""} />
                )}
              </Field>
              <Field label="ZIP" optional>
                {(control) => (
                  <Input
                    {...control}
                    name="postalCode"
                    defaultValue={profile?.address?.postalCode ?? ""}
                  />
                )}
              </Field>
            </div>
          </div>
        </Container>

        {/* Quotes carry the logo; a Builder's requests for quotes do not. */}
        {builder ? null : <LogoSection logoUrl={yard.logoUrl} canEdit={canEdit} />}

        {builder ? null : (
          <Container className="divide-y p-0">
            <SectionHeader
              title="Quote defaults"
              description="Applied to each new quote. Every quote can still be changed."
            />
            <div className="grid gap-5 px-6 py-5 sm:grid-cols-3">
              <Field label="Sales tax">
                {(control) => (
                  <Input
                    {...control}
                    name="tax"
                    inputMode="decimal"
                    suffix="%"
                    defaultValue={bpsToPercent(profile?.taxRateBps ?? 0)}
                  />
                )}
              </Field>
              <Field
                label="Markup on cost"
                hint="Used only for catalog items that have a cost and no price."
              >
                {(control) => (
                  <Input
                    {...control}
                    name="markup"
                    inputMode="decimal"
                    suffix="%"
                    defaultValue={bpsToPercent(profile?.defaultMarkupBps ?? 2500)}
                  />
                )}
              </Field>
              <Field label="Quotes valid for">
                {(control) => (
                  <Input
                    {...control}
                    name="validDays"
                    inputMode="numeric"
                    suffix="days"
                    defaultValue={profile?.quoteValidDays ?? 30}
                  />
                )}
              </Field>
              <Field label="Terms and conditions" optional className="sm:col-span-3">
                {(control) => (
                  <Textarea
                    {...control}
                    name="terms"
                    rows={5}
                    defaultValue={profile?.quoteTerms ?? ""}
                    placeholder="Prices subject to change. Quantities are estimates from the drawings provided and should be verified before ordering."
                  />
                )}
              </Field>
            </div>
          </Container>
        )}
        <Container className="divide-y p-0">
          <SectionHeader
            title="Review"
            description="Every takeoff is checked by our Reviewers. Choose whether that happens before or after it reaches you."
          />
          <div className="px-6 py-5">
            <RadioGroup
              value={reviewMode}
              onValueChange={(value) => setReviewMode(value as "gated" | "audit")}
            >
              <RadioGroup.ChoiceBox
                value="gated"
                label="Checked first"
                description="The takeoff waits for our Reviewers, then reaches you. The safer default."
              />
              <RadioGroup.ChoiceBox
                value="audit"
                label="Released at once, checked after"
                description="You get the takeoff the moment reading finishes. Takeoffs with low confidence or open questions still wait."
              />
            </RadioGroup>
          </div>
        </Container>
      </fieldset>

      {canEdit ? (
        <div className="flex justify-end">
          <Button type="submit" isLoading={save.isPending}>
            Save changes
          </Button>
        </div>
      ) : (
        <p className="txt-small px-1 text-ui-fg-muted">
          Only an Owner or Manager can change {builder ? "company" : "yard"} settings.
        </p>
      )}
    </form>
  );
}
